import React, { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../../state/authStore";
import { awardsApi, AwardsResponse, StickerView } from "../../api/awards";
import { ChildWithProgress } from "../WebShell";
import { WebMascot } from "../components/WebMascot";
import { StickerMedallion } from "../components/stickerVisuals";

type Props = {
  child: ChildWithProgress | null;
};

const GROUP_LABELS: Record<StickerView["group"], string> = {
  milestones: "Milestones",
  topics: "Topics mastered",
  streaks: "Streaks",
};
const GROUP_ORDER: StickerView["group"][] = ["milestones", "topics", "streaks"];

// Parent-facing sticker book — the child's earned + locked badges. Read-only;
// stickers are granted server-side as the kid answers quiz questions.
export function WebStickerBook({ child }: Props) {
  const { accessToken } = useAuthStore();
  const [data, setData] = useState<AwardsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accessToken || !child) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    awardsApi
      .list(child.id, accessToken)
      .then((r) => !cancelled && setData(r))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [accessToken, child]);

  const grouped = useMemo(() => {
    const g: Record<string, StickerView[]> = {};
    for (const s of data?.stickers ?? []) (g[s.group] ??= []).push(s);
    return g;
  }, [data]);

  const childName = child?.name ?? "your child";

  return (
    <div className="fadeup">
      <div className="w-section">
        <div className="w-section-h" style={{ alignItems: "baseline" }}>
          <h2>{childName}&apos;s sticker book</h2>
          {data && (
            <span
              style={{
                marginLeft: 12,
                fontSize: 14,
                fontWeight: 800,
                color: "var(--primary-ink)",
              }}
            >
              {data.earnedCount} / {data.totalCount} earned
            </span>
          )}
        </div>

        {loading ? (
          <p style={{ color: "var(--ink-3)", fontWeight: 600 }}>Loading…</p>
        ) : !data ? (
          <div className="w-empty">
            <WebMascot mood="happy" color="#FF8A4C" size={120} />
            <h2>No stickers yet</h2>
            <p>
              {childName} earns stickers by watching videos and answering quiz
              questions. They&apos;ll show up here.
            </p>
          </div>
        ) : (
          GROUP_ORDER.filter((grp) => (grouped[grp]?.length ?? 0) > 0).map(
            (grp) => (
              <div key={grp} style={{ marginBottom: 28 }}>
                <h3
                  style={{
                    fontSize: 13,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    marginBottom: 14,
                  }}
                >
                  {GROUP_LABELS[grp]}
                </h3>
                <div
                  className="w-respgrid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 14,
                  }}
                >
                  {grouped[grp].map((s) => (
                    <StickerTile key={s.key} sticker={s} />
                  ))}
                </div>
              </div>
            ),
          )
        )}
      </div>
    </div>
  );
}

function StickerTile({ sticker }: { sticker: StickerView }) {
  const earned = sticker.earned;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: 18,
        padding: "20px 14px 16px",
        textAlign: "center",
        opacity: earned ? 1 : 0.7,
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        <StickerMedallion stickerKey={sticker.key} earned={earned} size={62} />
      </div>
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 15,
          fontWeight: 800,
          color: "var(--ink)",
          marginBottom: 4,
        }}
      >
        {sticker.title}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)" }}>
        {sticker.description}
      </div>
    </div>
  );
}

export default WebStickerBook;
